import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import ContactSection from "../../components/ContactSection";

const DataCenter = () => {

    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            id: '01',
            title: 'Compute Solutions',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'Solaris designs and implements high-performance compute platforms optimised for virtualisation, cloud integration, and multi-tenant environments. Our services include capacity planning, performance tuning, hardware upgrades, and system expansions - enabling enterprises to scale compute resources without disrupting operations.',
            accent: '#2563eb'
        },
        {
            id: '02',
            title: 'Storage Systems',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="2" /><path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12" stroke="currentColor" strokeWidth="2" /><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" stroke="currentColor" strokeWidth="2" /></svg>
            ),
            description: 'Our storage solutions deliver secure, scalable, and high-availability data storage using SAN, NAS, and distributed architectures. We implement encryption, deduplication, automated tiering, and data migration services to improve performance and protect critical business information - with ongoing capacity planning to ensure long-term sustainability.',
            accent: '#0ea5e9'
        },
        {
            id: '03',
            title: 'Hyperconverged Infrastructure',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
            ),
            description: 'We deploy hyperconverged infrastructure that unifies compute, storage, and networking into a single, centrally managed platform. This simplifies data centre management, improves agility, and reduces operational costs - enabling seamless scalability and rapid deployment of new workloads across your environment.',
            accent: '#6366f1'
        },
        {
            id: '04',
            title: 'Full Stack Observability',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" /></svg>
            ),
            description: 'Gain complete visibility across your entire data centre stack with Solaris full stack observability solutions. We implement real-time monitoring, intelligent alerting, log analytics, and performance dashboards - providing actionable insights that enable proactive issue resolution and continuous infrastructure optimisation.',
            accent: '#f59e0b'
        },
        {
            id: '05',
            title: 'Application Performance Monitoring',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'Solaris delivers advanced application performance monitoring that tracks response times, transaction flows, and dependency health across your data centre applications. Our APM solutions identify bottlenecks, pinpoint root causes, and provide the intelligence needed to maintain optimal application performance at enterprise scale.',
            accent: '#10b981'
        },
        {
            id: '06',
            title: 'Backup, Archival & Encryption',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
            ),
            description: 'Protect your critical business data with Solaris enterprise-grade backup, archival, and encryption services. We design and manage automated backup schedules, offsite replication, immutable storage, and end-to-end encryption - ensuring your data remains secure, recoverable, and compliant with regulatory requirements.',
            accent: '#ef4444'
        }
    ];

    const servicesSpectrum = [
        'Compute & Virtualisation Platforms',
        'SAN & NAS Storage Solutions',
        'Hyperconverged Infrastructure',
        'Full Stack Observability',
        'Application Performance Monitoring',
        'Backup, Archival & Encryption',
        'Disaster Recovery & BCP',
        'Capacity Planning & Optimisation',
        'Data Centre Networking',
        'Managed Data Centre Services'
    ];

    const caseStudies = [
        {
            tag: 'Data Center',
            title: 'Network Modernisation And Infrastructure Transformation Programs',
            image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Data Center',
            title: 'Hyperconverged Infrastructure Rollout Reducing Operational Costs by 35%',
            image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&auto=format&fit=crop&q=80',
            dark: false
        },
        {
            tag: 'Data Center',
            title: 'Enterprise Storage Modernisation for High-Availability Financial Services',
            image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Data Center',
            title: 'Full Stack Observability Deployment Across Multi-Site Data Centre Environment',
            image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&auto=format&fit=crop&q=80',
            dark: false
        }
    ];

    const faqs = [
        {
            question: 'How does Solaris design and implement data centre infrastructure?',
            answer: 'Solaris follows a structured, discovery-led methodology for every data centre engagement. We begin with a comprehensive assessment of your current environment, workload requirements, and business objectives - then architect a tailored solution covering compute, storage, networking, and management. Our certified engineers handle full deployment, integration, and handover with minimal disruption to your operations.'
        },
        {
            question: 'Do you support hybrid and hyperconverged environments?',
            answer: 'Yes. Solaris has deep expertise in both hybrid and hyperconverged infrastructure deployments. We design and implement HCI solutions from leading vendors including Nutanix, VMware vSAN, and Cisco HyperFlex - as well as hybrid architectures that seamlessly bridge on-premises data centres with public and private cloud environments.'
        },
        {
            question: 'How do you ensure business continuity and disaster recovery?',
            answer: 'Business continuity and disaster recovery are core to every Solaris data centre engagement. We design multi-site replication strategies, implement automated failover, and develop tested recovery runbooks aligned to your RPO and RTO objectives. Our solutions leverage immutable backups, offsite archival, and encrypted data vaults to ensure your critical workloads can be restored rapidly following any disruption.'
        },
        {
            question: 'What monitoring and observability solutions do you offer?',
            answer: 'Solaris deploys comprehensive full stack observability platforms that provide real-time visibility across compute, storage, network, and application layers. We implement solutions including infrastructure monitoring, log aggregation, APM, and intelligent alerting - delivering a unified operations dashboard that enables proactive issue identification and faster mean time to resolution.'
        },
        {
            question: 'Do you provide post-deployment support and optimisation?',
            answer: 'Absolutely. Solaris offers flexible managed service models for ongoing data centre support, including 24/7 infrastructure monitoring, proactive capacity management, patch and firmware management, and dedicated incident response. Our post-deployment optimisation services ensure your environment continues to perform at peak efficiency as your business scales and requirements evolve.'
        },
        {
            question: 'Can Solaris help with data centre migration and consolidation?',
            answer: 'Yes. Solaris has extensive experience planning and executing data centre migrations and consolidations of all scales - from single-site refresh projects to full enterprise data centre transformations. We manage the entire lifecycle including assessment, design, workload migration, cutover coordination, and post-migration validation to ensure a seamless transition with zero data loss.'
        }
    ];

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{
                position: 'relative',
                width: '100%',
                minHeight: '480px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,20,50,0.72)' }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '120px 15px 80px', textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 style={{
                            color: '#fff', fontWeight: 700,
                            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                            lineHeight: 1.2, margin: '0 auto 20px',
                            maxWidth: '820px'
                        }}>
                            Infrastructure Designed for Continuity and Scale
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.78)', fontSize: '1rem',
                            lineHeight: 1.75, maxWidth: '520px', margin: '0 auto 36px'
                        }}>
                            Deliver resilient, high-performance data centre environments built to support your most demanding workloads - today and into the future
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <a href="#dc-overview" style={{ textDecoration: 'none' }}>
                                <button style={{
                                    display: 'inline-flex', alignItems: 'center',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    color: '#fff', border: 'none',
                                    padding: '14px 8px 14px 28px',
                                    borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                    cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: '0 8px 24px rgba(37,99,235,0.4)'
                                }}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                                >
                                    <span style={{ marginRight: '16px' }}>Know More</span>
                                    <span style={{
                                        background: '#fff', borderRadius: '50%',
                                        width: '38px', height: '38px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── WHAT IS DATA CENTER ── */}
            <section id="dc-overview" style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5">
                        {/* Left: image */}
                        <div className="col-lg-6">
                            <div style={{ position: 'relative' }}>
                                <img
                                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop&q=80"
                                    alt="Data Center Team"
                                    style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', height: '400px', display: 'block' }}
                                />
                            </div>
                        </div>

                        {/* Right: text */}
                        <div className="col-lg-6">
                            <p style={{
                                color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                WHAT IS DATA CENTER?
                            </p>
                            <h2 style={{
                                fontSize: 'clamp(1.9rem, 3vw, 2rem)', fontWeight: 700,
                                color: '#0f172a', lineHeight: 1.25, marginBottom: '28px', textAlign: 'left'
                            }}>
                                Infrastructure Designed for Continuity and Scale
                            </h2>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '20px', textAlign: 'left' }}>
                                Our data centre services deliver integrated compute, storage, networking, and monitoring solutions for modern enterprises. We support rack and blade servers, hyperconverged infrastructure, SAN and NAS storage, and high-speed data centre networking.
                            </p>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '32px', textAlign: 'left' }}>
                                Through <strong style={{ color: '#0f172a' }}>intelligent workload optimisation, multi-cloud readiness, and proactive monitoring</strong>, we help organisations improve availability, reduce risk, and scale with confidence.
                            </p>
                            <Link to="/contact" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)', color: '#fff',
                                padding: '13px 28px', borderRadius: '8px', fontWeight: 700,
                                fontSize: '0.9rem', textDecoration: 'none',
                                boxShadow: '0 6px 20px rgba(37,99,235,0.3)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.45)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.3)'; }}
                            >
                                Discuss Your Data Centre Project
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SOLUTIONS GRID ── */}
            <section style={{ padding: '110px 0', background: '#0b1120', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', top: '-120px', left: '-120px',
                    width: '500px', height: '500px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-100px', right: '-100px',
                    width: '440px', height: '440px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Core Offerings
                            </span>
                        </div>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 3vw, 2rem)', fontWeight: 600,
                            color: '#fff', lineHeight: 1.2, marginBottom: '20px'
                        }}>
                            Enterprise-Grade{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Data Center Capabilities
                            </span>
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
                            End-to-end data centre expertise across every layer - from compute and storage to observability, security, and disaster recovery.
                        </p>
                    </div>

                    <div className="row g-4">
                        {features.map((f, i) => (
                            <div key={f.id} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6 }}
                                    style={{
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.09)',
                                        borderRadius: '16px', padding: '36px 30px',
                                        height: '100%', cursor: 'default',
                                        transition: 'border-color 0.3s, background 0.3s',
                                        position: 'relative', overflow: 'hidden'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = f.accent + '55'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 0, left: '30px', right: '30px',
                                        height: '2px', borderRadius: '0 0 4px 4px',
                                        background: `linear-gradient(90deg, ${f.accent}, transparent)`
                                    }} />
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: f.accent + '20', border: `1px solid ${f.accent}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: f.accent, marginBottom: '22px'
                                    }}>
                                        {f.icon}
                                    </div>
                                    <div style={{
                                        fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)',
                                        letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px'
                                    }}>{f.id}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '14px', lineHeight: 1.4 }}>{f.title}</h3>
                                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: 0 }}>{f.description}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SERVICES SPECTRUM ── */}
            <section style={{ position: 'relative', overflow: 'hidden', background: '#0f172a', padding: '100px 0' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&auto=format&fit=crop&q=80')`,
                    backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.18
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        <div className="col-lg-5">
                            <p style={{
                                color: '#38bdf8', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                SERVICES SPECTRUM
                            </p>
                            <h2 style={{
                                color: '#fff', fontWeight: 800,
                                fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
                                lineHeight: 1.25, marginBottom: '20px'
                            }}>
                                The Complete Spectrum of Data Center Services
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.85 }}>
                                A comprehensive range of technologies and services covering every layer of modern data centre infrastructure - from physical hardware to intelligent monitoring and managed operations.
                            </p>
                        </div>
                        <div className="col-lg-7">
                            <div className="row g-3">
                                {servicesSpectrum.map((svc, i) => (
                                    <div key={i} className="col-md-6">
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: i * 0.07 }}
                                            viewport={{ once: true }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                padding: '16px 20px', borderRadius: '10px',
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseOver={e => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
                                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M5 13L9 17L19 7" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', fontWeight: 500 }}>{svc}</span>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY CHOOSE SOLARIS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            WHY SOLARIS
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '18px' }}>
                            Trusted Across Industries
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto' }}>
                            Delivering mission-critical data centre infrastructure with precision, engineering excellence, and an outcomes-first approach.
                        </p>
                    </div>

                    <div className="row g-4">
                        {[
                            { icon: '🏗️', title: 'Architecture-Led Design', desc: 'Every data centre engagement begins with a thorough assessment and bespoke architecture - built around your workloads, growth trajectory, and business continuity requirements.' },
                            { icon: '🔒', title: 'Security & Compliance Built In', desc: 'End-to-end encryption, access controls, audit logging, and regulatory alignment are embedded from day one - not added as an afterthought.' },
                            { icon: '🤝', title: 'Vendor-Agnostic Expertise', desc: 'Certified partnerships with HPE, Cisco, Dell, NetApp, Nutanix, Pure Storage, and more - we recommend the technology that best serves your objectives.' },
                            { icon: '⏱️', title: '24/7 Managed Support', desc: 'Round-the-clock infrastructure monitoring, proactive fault management, and rapid incident response - keeping your data centre environment always available.' },
                            { icon: '⚡', title: 'Proven Delivery Methodology', desc: 'Structured project frameworks and certified engineers ensure on-time, on-budget delivery across data centre builds, migrations, and transformation programmes.' },
                            { icon: '📊', title: 'Continuous Optimisation', desc: 'Ongoing capacity planning, performance analysis, and workload optimisation ensure your data centre infrastructure scales efficiently as your business evolves.' }
                        ].map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '32px 28px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default'
                                    }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)', borderColor: '#93c5fd' }}
                                >
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '14px',
                                        background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.6rem', marginBottom: '20px'
                                    }}>{item.icon}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: 0 }}>{item.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CASE STUDIES ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            CASE STUDIES
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '0' }}>
                            How Our Customers<br />Achieved Success
                        </h2>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={24}
                            slidesPerView={1}
                            navigation={{ prevEl: '.cs-dc-prev-btn', nextEl: '.cs-dc-next-btn' }}
                            pagination={{ clickable: true, el: '.cs-dc-pagination' }}
                            autoplay={{ delay: 4000, disableOnInteraction: false }}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1200: { slidesPerView: 3 } }}
                            className="cs-dc-swiper pb-5"
                        >
                            {caseStudies.map((cs, i) => (
                                <SwiperSlide key={i}>
                                    <motion.div
                                        style={{
                                            borderRadius: '14px', overflow: 'hidden',
                                            background: '#fff', border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                                        }}
                                        whileHover={{ y: -6, boxShadow: '0 20px 50px rgba(0,0,0,0.12)' }}
                                    >
                                        <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                                            <img src={cs.image} alt={cs.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <div style={{
                                                position: 'absolute', inset: 0,
                                                background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6) 100%)'
                                            }} />
                                            {cs.dark && (
                                                <div style={{
                                                    position: 'absolute', bottom: '16px', left: '16px', right: '16px',
                                                    color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.4
                                                }}>
                                                    Discover how we transformed enterprise infrastructure at scale
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '24px' }}>
                                            <span style={{
                                                fontSize: '0.75rem', fontWeight: 700, color: '#2563eb',
                                                textTransform: 'uppercase', letterSpacing: '1px',
                                                display: 'block', marginBottom: '10px'
                                            }}>+ {cs.tag}</span>
                                            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.5, marginBottom: '20px' }}>{cs.title}</h4>
                                            <Link to="/case-study" style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                background: '#fff', color: '#2563eb',
                                                border: '1.5px solid #2563eb', padding: '9px 20px',
                                                borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                                                textDecoration: 'none', transition: 'background 0.2s, color 0.2s'
                                            }}
                                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2563eb'; }}
                                            >
                                                Read More
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </Link>
                                        </div>
                                    </motion.div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
                            <button className="cs-dc-prev-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#475569', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <div className="cs-dc-pagination" style={{ display: 'flex', gap: '6px' }} />
                            <button className="cs-dc-next-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #2563eb', background: '#2563eb', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.borderColor = '#1d4ed8'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    </div>
                </div>

                <style>{`
                    .cs-dc-swiper .swiper-pagination-bullet {
                        width: 8px; height: 8px; background: #cbd5e1; opacity: 1;
                        border-radius: 50%; margin: 0 4px; transition: all 0.3s;
                    }
                    .cs-dc-swiper .swiper-pagination-bullet-active {
                        background: #2563eb; width: 24px; border-radius: 4px;
                    }
                `}</style>
            </section>

            {/* ── FAQ SECTION ── */}
            <section style={{ padding: '100px 0', background: '#0a0f1e', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.08) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <p style={{
                            color: '#60a5fa', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            FREQUENTLY ASKED QUESTIONS
                        </p>
                        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: '16px' }}>
                            What Would You Like To Know<br />About Data Center
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem' }}>
                            Everything you need to know about our services and solutions
                        </p>
                    </div>

                    <div className="row g-5 align-items-start">
                        {/* Left: Accordion */}
                        <div className="col-lg-7">
                            {faqs.map((faq, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    viewport={{ once: true }}
                                    style={{ marginBottom: '12px' }}
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        style={{
                                            width: '100%', textAlign: 'left',
                                            background: openFaq === i ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                                            border: openFaq === i ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: openFaq === i ? '12px 12px 0 0' : '12px',
                                            padding: '20px 24px',
                                            display: 'flex', alignItems: 'center', gap: '16px',
                                            cursor: 'pointer', transition: 'all 0.25s'
                                        }}
                                    >
                                        <span style={{
                                            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                                            background: openFaq === i ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : 'rgba(255,255,255,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.78rem', fontWeight: 700,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.25s'
                                        }}>
                                            {i + 1}
                                        </span>
                                        <span style={{
                                            flex: 1, fontSize: '0.98rem', fontWeight: 600,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.75)',
                                            lineHeight: 1.45, textAlign: 'left'
                                        }}>
                                            {faq.question}
                                        </span>
                                        <span style={{
                                            width: '24px', height: '24px', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: openFaq === i ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                                            fontSize: '1.4rem', lineHeight: 1, fontWeight: 300,
                                            transition: 'color 0.25s'
                                        }}>
                                            {openFaq === i ? '−' : '+'}
                                        </span>
                                    </button>

                                    {openFaq === i && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: 'rgba(99,102,241,0.07)',
                                                border: '1px solid rgba(99,102,241,0.4)',
                                                borderTop: 'none',
                                                borderRadius: '0 0 12px 12px',
                                                padding: '20px 24px 24px 72px'
                                            }}
                                        >
                                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                                                {faq.answer}
                                            </p>
                                        </motion.div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        {/* Right: CTA card */}
                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', position: 'relative',
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)',
                                    padding: '52px 40px', textAlign: 'center',
                                    minHeight: '360px', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '30px', left: '-20px', width: '90px', height: '90px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', top: '50px', left: '20px', width: '55px', height: '55px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '-20px', right: '40px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '28px', position: 'relative', zIndex: 1
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <rect x="2" y="3" width="20" height="14" rx="2" stroke="white" strokeWidth="2" />
                                        <path d="M8 21h8M12 17v4" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <h3 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(1.4rem, 2.5vw, 1.75rem)',
                                    lineHeight: 1.3, marginBottom: '16px', position: 'relative', zIndex: 1
                                }}>
                                    Didn't Find What You Were Looking For?
                                </h3>
                                <p style={{
                                    color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem',
                                    lineHeight: 1.7, marginBottom: '32px',
                                    position: 'relative', zIndex: 1, maxWidth: '280px'
                                }}>
                                    Our team of data centre experts is ready to help you with any questions or custom infrastructure requirements.
                                </p>
                                <Link to="/contact" style={{ textDecoration: 'none', position: 'relative', zIndex: 1 }}>
                                    <button style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                                        background: '#fff', color: '#4f46e5',
                                        border: 'none', padding: '14px 28px',
                                        borderRadius: '50px', fontWeight: 700, fontSize: '0.92rem',
                                        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.3)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                                    >
                                        Get In Touch With Our Experts
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12h14M12 5l7 7-7 7" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default DataCenter;
